import {useEffect, useState} from 'react';

const isSkippableType = (type: string): boolean => {
  const lower = type.toLowerCase();
  return lower.includes('gif') || lower.includes('svg');
};

const decode = async (
  blob: Blob
): Promise<{
  width: number;
  height: number;
  draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void;
  close: () => void;
}> => {
  // createImageBitmap rejects formats the <img> renderer still handles (HEIC in
  // Firefox/Safari), so fall through to the element decoder on failure.
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(blob);
      return {
        width: bitmap.width,
        height: bitmap.height,
        draw: (ctx, w, h) => ctx.drawImage(bitmap, 0, 0, w, h),
        close: () => bitmap.close(),
      };
    } catch {
      // Fall through to the element decoder.
    }
  }

  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Failed to decode image'));
      el.src = url;
    });
    return {
      width: img.width,
      height: img.height,
      draw: (ctx, w, h) => ctx.drawImage(img, 0, 0, w, h),
      close: () => undefined,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
};

// Downscales an S3-hosted image to a print-friendly JPEG so print-to-PDF
// embeds ~maxDimension bytes instead of the original multi-MB file.
export const useDownscaledImageSrc = (
  src: string,
  maxDimension = 1200
): string => {
  const [displaySrc, setDisplaySrc] = useState(src);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | undefined;
    setDisplaySrc(src);

    const run = async () => {
      try {
        const response = await fetch(src);
        if (!response.ok) return;
        const blob = await response.blob();
        if (isSkippableType(blob.type)) return;

        const image = await decode(blob);
        try {
          if (image.width <= maxDimension && image.height <= maxDimension) {
            return;
          }
          const scale = Math.min(
            maxDimension / image.width,
            maxDimension / image.height
          );
          const width = Math.round(image.width * scale);
          const height = Math.round(image.height * scale);

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) return;
          ctx.fillStyle = '#fff';
          ctx.fillRect(0, 0, width, height);
          image.draw(ctx, width, height);

          const jpeg = await new Promise<Blob | null>(resolve =>
            canvas.toBlob(resolve, 'image/jpeg', 0.85)
          );
          if (!jpeg || cancelled) return;

          objectUrl = URL.createObjectURL(jpeg);
          setDisplaySrc(objectUrl);
        } finally {
          image.close();
        }
      } catch (err) {
        console.warn('Image downscale failed; printing original', src, err);
      }
    };

    void run();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src, maxDimension]);

  return displaySrc;
};
